import { useState } from 'react';
import { salesReturnApi, invoicesApi } from '../api';
import { useData, submitForm, useSubmit } from '../hooks/useData';
import { Spinner, Modal, PageHeader, Field, Empty, EditBtn, Badge } from '../components/common';
import toast from 'react-hot-toast';
import { getBDDate } from '../utils/dateUtils';
import { downloadReport } from '../utils/downloadUtils';

export default function SalesReturnPage() {
  const { data, loading, reload } = useData(salesReturnApi.listAll);
  const { data: deliveredInvs }   = useData(invoicesApi.delivered);

  const [modal, setModal]       = useState(null); // 'return' | 'edit'
  const [selInv, setSelInv]     = useState(null);
  const [retLines, setRetLines] = useState([]);
  const [returnDate, setReturnDate] = useState(getBDDate());
  const [editRow, setEditRow]   = useState(null);
  const [loadingInv, setLoadingInv] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [doSubmit, saving] = useSubmit();

  // Open return form — load invoice with its item details
  const openReturn = async (inv) => {
    setLoadingInv(true);
    try {
      const res = await invoicesApi.getOne(inv.id);
      const full = res.data.data;
      setSelInv(full);
      setRetLines((full.details || []).map(d => ({
        item_id:    d.item_id,
        item_name:  d.item_name,
        orig_qty:   parseInt(d.quantity) || 0,
        unit_price: parseFloat(d.unit_price) || 0,
        discount:   parseFloat(d.discount_percent) || 0,
        return_qty: 0,
      })));
      setReturnDate(getBDDate());
      setEditRow(null);
      setModal('return');
    } catch { toast.error('Failed to load invoice details'); }
    finally { setLoadingInv(false); }
  };

  // Open edit — show existing return record
  const openEdit = async (row) => {
    setLoadingInv(true);
    try {
      const res = await invoicesApi.getOne(row.inv_id);
      const full = res.data.data;
      setSelInv(full);
      setEditRow(row);
      let lines = [];
      try { lines = JSON.parse(row.return_lines || '[]'); } catch {}
      setRetLines((full.details || []).map(d => {
        const existing = lines.find(l => String(l.item_id) === String(d.item_id));
        return {
          item_id:    d.item_id,
          item_name:  d.item_name,
          orig_qty:   parseInt(d.quantity) || 0,
          unit_price: parseFloat(d.unit_price) || 0,
          discount:   parseFloat(d.discount_percent) || 0,
          return_qty: existing ? parseInt(existing.return_qty) : 0,
        };
      }));
      setReturnDate(row.return_date || getBDDate());
      setModal('edit');
    } catch { toast.error('Failed to load return details'); }
    finally { setLoadingInv(false); }
  };

  const close = () => {
    setModal(null); setSelInv(null); setEditRow(null);
    setRetLines([]); setReturnDate(getBDDate());
  };

  const setQty = (idx, val) => {
    const parsed = parseInt(val) || 0;
    const max    = retLines[idx].orig_qty;
    if (parsed > max) {
      toast.error(`Cannot exceed original qty (${max}) for ${retLines[idx].item_name}`);
      return;
    }
    setRetLines(ls => ls.map((l, i) => i === idx ? { ...l, return_qty: parsed } : l));
  };

  // Amount = unit_price * qty * (1 - discount/100)
  const lineAmount = (l) =>
    Math.round((l.return_qty || 0) * (l.unit_price || 0) * (1 - (l.discount || 0) / 100) * 100) / 100;

  const totalReturnQty   = retLines.reduce((s, l) => s + (l.return_qty || 0), 0);
  const totalReturnValue = retLines.reduce((s, l) => s + lineAmount(l), 0);

  const handleSave = () => {
    if (totalReturnQty === 0) {
      toast.error('Please enter return quantity for at least one item');
      return;
    }
    const linesWithQty = retLines.filter(l => l.return_qty > 0);
    const payload = {
      inv_id:        selInv.id,
      return_date:   returnDate,
      quantity:      totalReturnQty,
      total_amount:  totalReturnValue.toFixed(2),
      return_lines:  JSON.stringify(linesWithQty.map(l => ({
        item_id: l.item_id, return_qty: l.return_qty
      }))),
    };
    if (modal === 'return') {
      doSubmit(() => salesReturnApi.create(payload), 'Sales return recorded — stock restored', reload, close);
    } else {
      doSubmit(() => salesReturnApi.update(editRow.id, payload), 'Return updated', reload, close);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadReport(salesReturnApi.exportExcel, 'sales-return-history.xlsx');
    } catch {
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Sales Returns" subtitle="Process customer returns" />

      {/* Delivered invoices available for return */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Delivered Invoices — Available for Return</span>
        </div>
        {deliveredInvs.length === 0
          ? <div className="card-body"><Empty msg="No delivered invoices available for return" /></div>
          : <div className="table-wrap">
              <table className="responsive-table">
                <thead><tr>
                  <th>Invoice #</th><th>Customer</th><th>Total</th><th>Action</th>
                </tr></thead>
                <tbody>{deliveredInvs.map(inv => (
                  <tr key={inv.id}>
                    <td data-label="Invoice #">
                      <div className="td-primary font-mono">{inv.inv_number}</div>
                      <div className="td-muted">{inv.inv_date}</div>
                    </td>
                    <td data-label="Customer">
                      <div>{inv.customer_name}</div>
                      <div className="td-muted font-mono">{inv.customer_mobile}</div>
                    </td>
                    <td data-label="Total" className="font-mono">৳{Number(inv.total_price).toLocaleString()}</td>
                    <td data-label="Action">
                      <button className="btn btn-danger btn-sm"
                        onClick={() => openReturn(inv)} disabled={loadingInv}>
                        Return
                      </button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>}
      </div>

      {/* Return history */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Return History</span>
          <button className="btn btn-secondary btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
        {loading ? <Spinner /> : (
          <div className="table-wrap">
            {data.length === 0 ? <Empty /> : (
              <table className="responsive-table">
                <thead><tr>
                  <th>Invoice #</th><th>Customer</th>
                  <th>Qty</th><th>Value</th>
                  <th>Date</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>{data.map(r => (
                  <tr key={r.id}>
                    <td data-label="Invoice #" className="td-primary font-mono">{r.inv_number}</td>
                    <td data-label="Customer">{r.customer_name}</td>
                    <td data-label="Qty" className="font-mono">{r.quantity}</td>
                    <td data-label="Value" className="font-mono">৳{Number(r.total_amount).toLocaleString()}</td>
                    <td data-label="Date" className="text-muted">{r.return_date}</td>
                    <td data-label="Status"><Badge status="returned" /></td>
                    <td data-label="Actions"><div className="action-cell">
                      <EditBtn onClick={() => openEdit(r)} />
                    </div></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Return / Edit Modal */}
      {modal && selInv && (
        <Modal
          title={modal === 'return' ? 'Sales Return' : 'Edit Return'}
          onClose={close} size="modal-lg"
          footer={<>
            <button className="btn btn-secondary" onClick={close}>Cancel</button>
            <button className="btn btn-danger" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : modal === 'return' ? 'Submit Return' : 'Save Changes'}
            </button>
          </>}>

          {/* Invoice summary — mirrors Purchase Return's PO summary panel */}
          <div style={{ marginBottom: 18, padding: '14px 16px', background: 'var(--c-accent-tint)', border: '1px solid var(--c-border)', borderLeft: '3px solid var(--c-accent)', borderRadius: 'var(--r-sm)' }}>
            <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--c-text)', marginBottom: 10 }}>
              {selInv.inv_number}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '4px 24px', fontSize: '.88rem', color: 'var(--c-text-2)' }}>
              <span><strong>Customer:</strong> {selInv.customer_name}</span>
              <span><strong>Mobile:</strong> {selInv.customer_mobile}</span>
              <span><strong>Invoice Date:</strong> {selInv.inv_date}</span>
              <span><strong>Invoice Total:</strong> ৳{Number(selInv.total_price).toLocaleString()}</span>
            </div>
          </div>

          {/* Per-item return quantities — same table layout as Purchase Return */}
          <div className="section-label">Return Quantity per Item</div>
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table className="detail-table">
              <thead><tr>
                <th style={{ minWidth: 200 }}>Item Name</th>
                <th style={{ minWidth: 90,  textAlign: 'right' }}>Orig Qty</th>
                <th style={{ minWidth: 100, textAlign: 'right' }}>Unit Price</th>
                <th style={{ minWidth: 80,  textAlign: 'right' }}>Disc%</th>
                <th style={{ minWidth: 120 }}>Return Qty</th>
                <th style={{ minWidth: 110, textAlign: 'right' }}>Return Value</th>
              </tr></thead>
              <tbody>
                {retLines.map((line, idx) => (
                  <tr key={line.item_id}>
                    <td className="td-primary">{line.item_name}</td>
                    <td style={{ textAlign: 'right' }} className="font-mono">{line.orig_qty}</td>
                    <td style={{ textAlign: 'right' }} className="font-mono">৳{Number(line.unit_price).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }} className="text-muted">{line.discount}%</td>
                    <td>
                      <input
                        type="number"
                        value={line.return_qty}
                        min="0"
                        max={line.orig_qty}
                        onChange={e => setQty(idx, e.target.value)}
                        style={{ width: 90 }}
                      />
                      <span style={{ fontSize: '.75rem', color: 'var(--c-text-3)', marginLeft: 6 }}>
                        / {line.orig_qty}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }} className="font-mono">
                      ৳{lineAmount(line).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {retLines.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: 'var(--c-text-3)' }}>
                    No item details found for this invoice.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals row — same as Purchase Return */}
          {totalReturnQty > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginBottom: 16, padding: '10px 14px', background: 'var(--c-bg)', borderRadius: 'var(--r-sm)', border: '1px solid var(--c-border)', fontSize: '.9rem' }}>
              <span>Total Return Qty: <strong className="font-mono">{totalReturnQty}</strong></span>
              <span>Total Return Value: <strong className="font-mono text-danger">৳{totalReturnValue.toLocaleString()}</strong></span>
            </div>
          )}

          <Field label="Return Date">
            <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} />
          </Field>
        </Modal>
      )}
    </div>
  );
}